import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { BooleanAspect } from '@adornis/filter/aspect-schemas/BooleanAspect.js';
import { DateTimeAspectSchema } from '@adornis/filter/aspect-schemas/DateTimeAspect.js';
import { ListAspect, ListAspectSchema } from '@adornis/filter/aspect-schemas/ListAspect.js';
import { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import { BillingRhythm } from '../../../billingPositions/entities/BillingPosition.js';
import { ClassBookingFilterAspectSchema } from '../../../bookings/api/ClassBookingFilterAspect.js';
import { Grade } from '../../../bookings/entities/ClassBooking.js';
import {
  Gender,
  GermanInsuranceCompanies,
  GermanInsuranceTypes,
  Konfession,
  NamensTitel,
  Religion,
  SchwerbehinderungsGrad,
  ZahlungsRythmus,
} from '../../../db/enums/misc-enums.js';
import { Namensvorsatz } from '../../../db/enums/namensvorsatz.js';
import { Namenszusatz } from '../../../db/enums/namenszusatz.js';
import { Group } from '../../../groups/entities/Group.js';
import { Kostenstelle } from '../../../groups/entities/Kostenstelle.js';
import { School } from '../../../groups/entities/School.js';
import { GroupFilterAspectSchema } from '../../../groups/GroupFilterAspect.js';
import { RoleFilterAspectSchema } from '../../../permissions/api/RoleFilterAspect.js';
import { Abrechnungsgruppe, PersonenGruppe } from '../../employees/entities/employee-sections/an-beschaeftigung.js';
import {
  BetriebsnummerAbweichendeKrankenkasseBeiGeringfuegig,
  VersicherungsstatusKurzfristigBeschaeftigte,
} from '../../employees/entities/employee-sections/an-geringfuegigebeschaeftigung.js';
import { SuSBewerberStatus } from '../../students/schueler-daten.js';
import { SepaActiveAspectSchema } from '../../../sepa/api/SepaActiveAspect.js';

/**
 * Specifies custom aspects for some of the fields in the user list.
 * These aspects are used to provide additional filtering options for specific fields.
 */
export const UserListCustomFilterFields = {
  roles: RoleFilterAspectSchema({
    filter: new AdornisFilter({
      aspects: [
        new BooleanAspect({ customID: 'onlyInternal', value: true }),
        new ListAspect({ fieldName: '_class', values: [School._class] }),
      ],
    }),
  }),
  buchungen: ClassBookingFilterAspectSchema(),
  bookingsOfChildren: ClassBookingFilterAspectSchema(),
  activeBuchungen: ClassBookingFilterAspectSchema({ showTimeSelector: false }),
  activeBuchungenOfChildren: ClassBookingFilterAspectSchema({ showTimeSelector: false }),
  'roles.contexts': GroupFilterAspectSchema({
    filter: new AdornisFilter({ aspects: [new StringAspect({ fieldName: '_class', value: School._class })] }),
  }),
  'profilDaten.stammdaten.geschlecht': ListAspectSchema(Object.values(Gender), 'Geschlecht'),
  'profilDaten.stammdaten.titel': ListAspectSchema(Object.values(NamensTitel), 'Titel'),
  'profilDaten.stammdaten.religion': ListAspectSchema(Object.values(Religion), 'Religion'),
  // 'profilDaten.stammdaten.namenszusatz': ListAspectSchema(Object.values(Namenszusatz), 'Namenszusatz'),
  // 'profilDaten.stammdaten.vorsatzWort': ListAspectSchema(Object.values(Namensvorsatz), 'Vorsatzwort'),
  'profilDaten.spende.zahlungsRythmus': ListAspectSchema(Object.values(ZahlungsRythmus), 'Zahlungsrythmus'),
  'mitarbeiterDaten.anSteuerInfos.verfuegbarkeit': ListAspectSchema(
    ['Hauptarbeitgeber', 'Nebenarbeitgeber'],
    'Verfuegbarkeit',
  ),
  'mitarbeiterDaten.anSteuerInfos.steuerklasse': ListAspectSchema(['1', '2', '3', '4', '5', '6'], 'Steuerklasse'),
  'mitarbeiterDaten.anSteuerInfos.steuerklassePartner': ListAspectSchema(
    ['1', '2', '3', '4', '5', '6'],
    'Steuerklasse Partner',
  ),
  'mitarbeiterDaten.anSteuerInfos.konfession': ListAspectSchema(Object.values(Konfession), 'Konfession'),
  'mitarbeiterDaten.anSteuerInfos.konfessionPartner': ListAspectSchema(Object.values(Konfession), 'Konfession Partner'),
  'mitarbeiterDaten.anSozialVersicherung.versicherungsStatus': ListAspectSchema(
    Object.values(GermanInsuranceTypes),
    'Versicherungsstatus',
  ),
  'mitarbeiterDaten.anSozialVersicherung.geringfuegigKrankenkasseBetriebsnummer': ListAspectSchema(
    Object.values(BetriebsnummerAbweichendeKrankenkasseBeiGeringfuegig),
    'Betriebsnummer abweicher Krankenkasse bei Geringverdiener',
  ),
  'mitarbeiterDaten.anSozialVersicherung.versicherungsstatusKurzfristigBeschaeftigt': ListAspectSchema(
    Object.values(VersicherungsstatusKurzfristigBeschaeftigte),
    'Versicherungsstatus kurzfristige Beschäftigte',
  ),
  'mitarbeiterDaten.anPrivatVersicherung.letzteGesKrankenkasse': ListAspectSchema(
    Object.values(GermanInsuranceCompanies),
    'Letzte gesetzte Krankenkasse',
  ),
  'mitarbeiterDaten.anGeringfuegigeBeschaeftigung.beschaeftigungen.entlohnungsArt': ListAspectSchema(
    ['geringfügig entlohnt', 'mehr als geringfügig entlohnt'],
    'Entlohnungsart',
  ),
  'mitarbeiterDaten.anSchwerbehinderung.grad': ListAspectSchema(
    Object.values(SchwerbehinderungsGrad),
    'Schwerbehinderungsgrad',
  ),
  'mitarbeiterDaten.anBeschaeftigung.personenGruppe': ListAspectSchema(Object.values(PersonenGruppe), 'Personengruppe'),
  'mitarbeiterDaten.anBeschaeftigung.abrechnungsgruppe': ListAspectSchema(
    Object.values(Abrechnungsgruppe),
    'Abrechnungsgruppe',
  ),
  'activeBuchungen.groups': GroupFilterAspectSchema({
    operatingFieldType: 'object',
    filter: new AdornisFilter({
      aspects: [new ListAspect({ fieldName: '_class', values: [Group._class, Kostenstelle._class] })],
    }),
  }),
  'buchungen.groups': GroupFilterAspectSchema({ operatingFieldType: 'object' }),
  'bookingsOfChildren.groups': GroupFilterAspectSchema({ operatingFieldType: 'object' }),
  'activeBuchungenOfChildren.groups': GroupFilterAspectSchema({ operatingFieldType: 'object' }),
  'activeBuchungen.grade': ListAspectSchema(Object.values(Grade), 'Jahrgangsstufe'),
  'buchungen.grade': ListAspectSchema(Object.values(Grade), 'Jahrgangsstufe'),
  'bookingsOfChildren.grade': ListAspectSchema(Object.values(Grade), 'Jahrgangsstufe'),
  'activeBuchungenOfChildren.grade': ListAspectSchema(Object.values(Grade), 'Jahrgangsstufe'),
  'sepaMandats.rhythm': ListAspectSchema(Object.values(BillingRhythm), 'Rhythmus'),
  'schuelerDaten.wantedKlassenstufe': ListAspectSchema(Object.values(Grade), 'Wunschklasse'),
  'schuelerDaten.currentKlassenstufeAtBewerbungsDate': ListAspectSchema(
    Object.values(Grade),
    'Aktuelle Klassenstufe zum Bewerbungsdatum',
  ),
  'schuelerDaten.susBewerberStatus': ListAspectSchema(Object.values(SuSBewerberStatus), 'SUS Bewerberstatus'),
  contractApplyDate: DateTimeAspectSchema('is'),
  'sepaMandats.active': SepaActiveAspectSchema,
};
